import React from 'react';
import { Element } from 'react-scroll';
import { useAsync } from 'react-use';
import { always, propEq, ifElse } from 'ramda';
import { useRouter } from 'next/router';

import { Characteristic } from '@definitions/characteristics.types';
import { ContentBody } from '@components/contentBody';
import { Block, Header } from '@definitions/common.types';
import { BLOCK_TYPE, NoArtworks, ParagraphWithTitle } from '@components/blocks';
import { ContinueReadingButton, ButtonContainer } from '@components/continueReadingButton';
import { useMonogram } from '@hooks/useMonogram';
import { renderWhenTrue } from '@utils/rendering';
import { getHasProducts } from '@utils/helpers';
import { useTrackConversions } from '@hooks/useTrackConversions';
import { CONVERSIONS_EVENTS, VIEW_CONTENT_TYPES } from '@definitions/conversions.types';
import { DEFAULT_BACKGROUND_COLOR, DEFAULT_TEXT_COLOR } from '@utils/constants';
import { ArticleHeader } from '@components/articles/articleHeader';

import { Container, HeaderWrapper } from './characteristicView.styles';

export interface CharacteristicViewProps {
  characteristic: Characteristic;
}
const CONTENT_BODY_ELEMENT_NAME = 'characteristic-body';

export const CharacteristicView = ({ characteristic }: CharacteristicViewProps) => {
  const {
    monogram,
    textColor = DEFAULT_TEXT_COLOR,
    backgroundColor = DEFAULT_BACKGROUND_COLOR,
    products,
    showContinuedReading,
    body,
    shoppable,
    accessToken,
  } = characteristic;

  const { addMonogram, clearMonogram } = useMonogram();
  const { sendConversion } = useTrackConversions();
  const hasProducts = getHasProducts(products);
  const router = useRouter();
  const isExclusive = router.pathname === '/exclusive/[slug]';

  useAsync(async () => {
    const id = new Date().getTime();

    if (monogram?.monogramLetter?.length) {
      const { monogramLetter, monogramColor, monogramAnimationType } = monogram;

      addMonogram({
        letter: monogramLetter,
        color: monogramColor,
        appearance: monogramAnimationType,
        id,
      });
    }

    await sendConversion({
      eventType: CONVERSIONS_EVENTS.VIEW_CONTENT,
      contentName: VIEW_CONTENT_TYPES.CHARACTERISTIC_PLP,
    });

    return () => {
      clearMonogram(id);
    };
  }, []);

  const renderArticleHeader = (header: Header, index: number) => <ArticleHeader header={header} key={index} />;
  const renderParagraphHeader = (header: Block, index: number) => (
    <HeaderWrapper key={index}>
      <ParagraphWithTitle {...header.value} isContinuReadingClicked={true} />
    </HeaderWrapper>
  );
  const renderHeader = ifElse(propEq('type', BLOCK_TYPE.HEADER), renderParagraphHeader, renderArticleHeader);

  const renderCharacteristicProducts = (hasProducts: boolean, isExclusive: boolean) => {
    if (hasProducts) {
      return (
        <ContentBody
          body={products}
          hasSpacingTop={false}
          hasSpacingBottom={false}
          textColor={textColor}
          backgroundColor={backgroundColor}
          shoppable={shoppable}
          accessToken={accessToken}
        />
      );
    }
    return isExclusive ? null : <NoArtworks />;
  };

  const renderContinueReadingButton = renderWhenTrue(
    always(
      <ButtonContainer>
        <ContinueReadingButton elementName={CONTENT_BODY_ELEMENT_NAME} />
      </ButtonContainer>
    )
  );

  return (
    <Container backgroundColor={backgroundColor} textColor={textColor}>
      {characteristic.header.map(renderHeader)}
      {renderContinueReadingButton(false)}
      {renderCharacteristicProducts(hasProducts, isExclusive)}
      <Element name={CONTENT_BODY_ELEMENT_NAME}>
        <ContentBody
          body={body}
          textColor={textColor}
          backgroundColor={backgroundColor}
          shoppable={shoppable}
          accessToken={accessToken}
        />
      </Element>
    </Container>
  );
};
